"use client";

import Image from "next/image";
import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { HubSpotForm } from "@/components/ui/hubspot-form";
import { HUBSPOT_PORTAL_ID, HUBSPOT_REGION, HUBSPOT_FORMS } from "@/data/hubspot-forms";
import { cn } from "@/lib/utils";
import { TestimonialsSection } from "@/components/home/testimonials-section";
import { StatsBarSection } from "@/components/home/stats-bar";
import { InstallationTypesSection } from "@/components/home/installation-types";
import { Button } from "@/components/ui/button";
import { LazySection } from "@/components/performance/lazy-section";

type Variant = "main" | "financing" | "presupuestos";

type Props = {
  locale: string;
  variant: Variant;
};

const variantCopy = (
  t: ReturnType<typeof useTranslations<"quotePage">>,
  variant: Variant,
) => ({
  h1: t("h1"),
  h2: t("h2"),
  title: t(`variants.${variant}.title`),
  eyebrow: t(`variants.${variant}.eyebrow`),
  heroCopy: t(`variants.${variant}.heroCopy`),
  secondaryCopy: t(`variants.${variant}.secondaryCopy`),
  cta: t("cta"),
});

const renderParagraphWithBold = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*)/g).filter(Boolean);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={`${index}-${part}`} className="font-semibold text-gray-900 dark:text-white">
          {part.slice(2, -2)}
        </strong>
      );
    }

    return <span key={`${index}-${part}`}>{part}</span>;
  });
};

export default function QuotePage({ locale, variant }: Props) {
  void locale;
  const t = useTranslations("quotePage");
  const tHome = useTranslations("home");
  const copy = variantCopy(t, variant);
  const installationTypesTitle =
    variant === "presupuestos" ? t("h2InstallationTypes") : undefined;
  const testimonialsTitle = variant === "presupuestos" ? t("h2Testimonials") : undefined;

  const paragraphs = useMemo(() => {
    const raw = t.raw("content.paragraphs");
    return Array.isArray(raw) ? (raw as string[]) : [];
  }, [t]);

  const bullets = useMemo(() => {
    const raw = t.raw("content.bullets");
    return Array.isArray(raw) ? (raw as string[]) : [];
  }, [t]);

  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100 dark:from-surface-dark-raised dark:via-surface-dark-raised dark:to-surface-dark text-gray-900 dark:text-gray-100">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="h-full w-full bg-[radial-gradient(circle_at_30%_30%,rgba(208,27,23,0.12),transparent_50%),radial-gradient(circle_at_70%_20%,rgba(12,59,82,0.15),transparent_45%)] animate-[pulse_8s_ease-in-out_infinite]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,transparent_0%,rgba(255,255,255,0.03)_50%,transparent_100%)] dark:bg-[linear-gradient(to_right,transparent_0%,rgba(255,255,255,0.01)_50%,transparent_100%)]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="flex flex-col gap-6 sm:gap-8 mb-8 sm:mb-12">
            <span className="text-xs sm:text-sm uppercase tracking-label text-red-600 dark:text-red-400 font-bold animate-[fadeIn_0.6s_ease-out]">
              {copy.eyebrow}
            </span>
            <div className="flex flex-wrap gap-3 animate-[fadeIn_0.8s_ease-out]">
              <Button asChild className="w-full sm:w-auto whitespace-normal text-center leading-tight h-auto min-h-12 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white rounded-full px-4 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base font-bold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <a href="#cta-form">{t("cta")}</a>
              </Button>
            </div>
          </div>
          
          <div className="grid gap-8 lg:gap-12 xl:gap-16 lg:grid-cols-12 items-start">
            <div className="lg:col-span-5 xl:col-span-6 space-y-6 sm:space-y-8 animate-[fadeIn_1s_ease-out]">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-tight bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent">
                {copy.h1}
              </h1>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-800 dark:text-gray-200 leading-relaxed">
                {copy.h2}
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-700 dark:text-gray-300 leading-relaxed">{copy.heroCopy}</p>
              
              <div className="rounded-2xl sm:rounded-card border-2 border-gray-200 dark:border-gray-700 bg-gradient-to-br from-white/90 to-white/70 dark:from-surface-dark-raised/90 dark:to-surface-dark-raised/70 backdrop-blur-sm p-4 sm:p-5 flex items-center gap-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]">
                <div className="relative h-14 w-14 sm:h-16 sm:w-16 rounded-xl sm:rounded-2xl overflow-hidden flex-shrink-0 ring-2 ring-orange-500/20">
                  <Image
                    src="/static/misc/producto-estrella-fallback.webp"
                    alt={t("media.heroAlt")}
                    fill
                    sizes="(max-width: 640px) 56px, 64px"
                    className="object-cover"
                  />
                </div>
                <div className="text-sm sm:text-base text-gray-700 dark:text-gray-200">
                  <p className="font-bold mb-1">{copy.cta}</p>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    {copy.secondaryCopy}
                  </p>
                </div>
              </div>
              
              <div
                className="group rounded-2xl border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-white/90 to-gray-50/80 dark:from-surface-dark-raised/80 dark:to-surface-dark-raised/80 backdrop-blur-sm p-4 sm:p-5 shadow-md hover:shadow-lg transition-all duration-300 hover:border-orange-300 dark:hover:border-orange-700"
              >
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-200 leading-relaxed">
                  {renderParagraphWithBold(paragraphs[0] ?? "")}
                </p>
              </div>
            </div>

            <div className="lg:col-span-7 xl:col-span-6 lg:sticky lg:top-24 animate-[fadeIn_1.2s_ease-out]">
              <div className="rounded-card sm:rounded-[2rem] bg-gradient-to-br from-white via-white to-gray-50/80 dark:from-surface-dark-raised dark:via-surface-dark-raised dark:to-surface-dark-raised border-2 border-gray-200 dark:border-gray-700 shadow-card-lg dark:shadow-card-dark hover:shadow-card-lg dark:hover:shadow-card-dark transition-all duration-500 p-5 sm:p-6 lg:p-7 xl:p-8">
                <div className="mb-6">
                  <h3 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-3 leading-tight">{copy.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-relaxed">{copy.secondaryCopy}</p>
                </div>
                <HubSpotForm
                  portalId={HUBSPOT_PORTAL_ID}
                  formId={HUBSPOT_FORMS.freeStudyOrganicHeader}
                  region={HUBSPOT_REGION}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <InstallationTypesSection title={installationTypesTitle} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 section-y lg:py-24">
        <div className="grid gap-8 sm:gap-10 lg:gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7 space-y-5 sm:space-y-6">
            {paragraphs.map((para, idx) => (
              idx > 0 && (
              <p 
                key={idx} 
                className="text-base sm:text-lg lg:text-xl text-gray-800 dark:text-gray-200 leading-relaxed sm:leading-loose border-l-4 border-orange-500 pl-4 sm:pl-6 py-1 bg-gradient-to-r from-orange-50/50 to-transparent dark:from-orange-950/20 dark:to-transparent rounded-r-lg"
              >
                {renderParagraphWithBold(para)}
              </p>
            )))}
          </div>
          <div className="lg:col-span-5 space-y-6 sm:space-y-8">
            <div className="rounded-2xl sm:rounded-card border-2 border-gray-200 dark:border-gray-700 bg-gradient-to-br from-white to-gray-50/80 dark:from-surface-dark-raised dark:to-surface-dark-raised p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
              <h4 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-5 pb-3 border-b-2 border-orange-500/30">{copy.cta}</h4>
              <ul className="space-y-3 text-sm sm:text-base text-gray-700 dark:text-gray-300">
                {bullets.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 group">
                    <span className="text-orange-600 dark:text-orange-400 font-bold text-lg flex-shrink-0 group-hover:scale-125 transition-transform duration-200">✓</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="overflow-hidden rounded-2xl sm:rounded-card bg-gradient-to-br from-red-600 via-orange-600 to-amber-500 shadow-2xl transform hover:scale-[1.02] transition-all duration-300 ring-4 ring-orange-500/20">
              <Image
                src="/static/home/hero-illustration.png"
                alt={t("media.secondaryAlt")}
                width={900}
                height={600}
                className={cn("w-full h-full object-cover opacity-95 hover:opacity-100 transition-opacity duration-300")}
              />
            </div>
          </div>
        </div>
      </section>

      <LazySection minHeightClassName="min-h-[180px]">
        <StatsBarSection />
      </LazySection>
      
      <section className="bg-gradient-to-b from-white to-gray-50 dark:from-surface-dark dark:to-surface-dark-raised px-4 section-y lg:py-24">
        <div className="max-w-7xl mx-auto">
          <TestimonialsSection t={tHome} title={testimonialsTitle} />
        </div>
      </section>

      <section id="cta-form" className="relative overflow-hidden bg-gradient-to-br from-brand-navy via-[#0a4159] to-brand-navy-dark px-4 section-y text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(249,115,22,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom_right,transparent_0%,rgba(0,0,0,0.2)_100%)]" />
        
        <div className="relative max-w-7xl mx-auto">
          <div className="flex flex-col lg:grid lg:grid-cols-[minmax(0,0.88fr)_minmax(0,1.12fr)] gap-8 lg:gap-12 items-start">
            <div className="w-full space-y-4 sm:space-y-5 lg:space-y-6 order-2 lg:order-1">
              <p className="text-xs sm:text-sm uppercase tracking-label text-amber-300 font-bold animate-[pulse_2s_ease-in-out_infinite]">
                {copy.eyebrow}
              </p>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-black leading-tight bg-gradient-to-r from-white via-amber-50 to-white bg-clip-text text-transparent">
                {copy.title}
              </h3>
              <p className="text-sm sm:text-base lg:text-lg text-gray-100 leading-relaxed">
                {copy.secondaryCopy}
              </p>
              <ul className="space-y-2.5 sm:space-y-3 text-sm sm:text-base text-gray-100 pt-2">
                {bullets.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2.5 sm:gap-3 group">
                    <span className="text-amber-400 font-bold text-base sm:text-lg flex-shrink-0 group-hover:scale-125 transition-transform duration-200 mt-0.5">✓</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="w-full order-1 lg:order-2 lg:sticky lg:top-4">
              <div className="w-full rounded-2xl sm:rounded-card bg-white text-gray-900 p-5 sm:p-6 lg:p-7 xl:p-8 shadow-card-dark border-2 border-white/30 backdrop-blur-sm hover:shadow-card-dark transition-all duration-300">
                <HubSpotForm
                  portalId={HUBSPOT_PORTAL_ID}
                  formId={HUBSPOT_FORMS.freeStudyOrganicFooter}
                  region={HUBSPOT_REGION}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
